import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  get,
  set,
  initialise,
  doesPathAcceptsBanner,
  getAppStoreUrl,
  getCorrespondingAppUrl,
  detectPlatform,
  validate,
  isWindowHidden,
  getDefaultPreferences,
  getOpenAppPreferences,
  getDiscardPreferences,
  getOpenAppSuccesPreferences,
  TIMEOUT_OPEN_IN_APP,
  OPEN_IN_APP_DEFAULT_ACTION,
  OPEN_IN_APP_USER_ACTION,
  OPEN_IN_APP_UNEXPECTED_USER_ACTION
} from "@src/template/helpers/nativeAppBanner";
import { isAndroid, isIos } from "@src/helpers/deviceDetection";
import styles from "./index.css";
import deferredStyles from "./deferred.css";
import withTranslation from "../translation";
import { trackNativeAppBannerGetApp } from "@src/helpers/events";
import { isWindowDefined } from "@src/helpers/url";

let timeout;
class NativeAppBanner extends PureComponent {
  constructor() {
    super();
    const isSupportedPlatform = !!getAppStoreUrl(),
      osPlatform = detectPlatform();
    this.state = {
      showBanner: { display: "none" },
      isSupportedPlatform: isSupportedPlatform,
      osPlatform: osPlatform,
      nativeBannerSettings: getDefaultPreferences()
    };

    this.hideBanner = this.hideBanner.bind(this);
    this.showBanner = this.showBanner.bind(this);
    this.discardBanner = this.discardBanner.bind(this);
    this.handleGetApp = this.handleGetApp.bind(this);
    this.openInAppCallback = this.openInAppCallback.bind(this);
    this.handleMobileAppUrlsAndroid = this.handleMobileAppUrlsAndroid.bind(
      this
    );
    this.browserVisibilityChangeAndroid = this.browserVisibilityChangeAndroid.bind(
      this
    );

    if (isWindowDefined() && isSupportedPlatform) {
      if (isAndroid()) {
        window.addEventListener(
          "beforeunload",
          this.handleMobileAppUrlsAndroid
        );
        window.addEventListener(
          "visibilitychange",
          this.browserVisibilityChangeAndroid
        );
      }
    }
  }

  browserVisibilityChangeAndroid() {
    if (!this.state.isSupportedPlatform || !isWindowHidden()) {
      return;
    }
    const preferences = get("asos.siteChrome.nativeBanner");
    if (isWindowHidden() && preferences?.expireOpenInApp >= Date.now()) {
      clearTimeout(timeout);
      const prefUpdate = getOpenAppSuccesPreferences();
      this.setState(() => ({
        nativeBannerSettings: prefUpdate
      }));
      set("asos.siteChrome.nativeBanner", prefUpdate);
    }
  }

  handleMobileAppUrlsAndroid() {
    const preferences = get("asos.siteChrome.nativeBanner");
    if (
      preferences == null ||
      preferences?.openInApp === OPEN_IN_APP_DEFAULT_ACTION
    ) {
      return;
    }
    const { expireOpenInApp, openInApp } = preferences;
    if (
      expireOpenInApp >= Date.now() &&
      openInApp === OPEN_IN_APP_UNEXPECTED_USER_ACTION
    ) {
      clearTimeout(timeout);
      set("asos.siteChrome.nativeBanner", getDefaultPreferences());
    }
    if (openInApp === OPEN_IN_APP_USER_ACTION) {
      const resetAction = this.resetBannerOnTimeout(
        expireOpenInApp,
        this.showBanner
      );
      timeout = setTimeout(resetAction, TIMEOUT_OPEN_IN_APP);
      set(
        "asos.siteChrome.nativeBanner",
        Object.assign({}, preferences, {
          openInApp: OPEN_IN_APP_UNEXPECTED_USER_ACTION
        })
      );
    }
  }

  componentWillMount() {
    const preferences = get("asos.siteChrome.nativeBanner");
    if (preferences != null) {
      this.setState(() => ({
        nativeBannerSettings: preferences
      }));
    }
  }

  componentWillReceiveProps(newProps) {
    const url = getCorrespondingAppUrl(
      this.state.isSupportedPlatform,
      this.props.storeCode
    );
    if (validate(this.state.isSupportedPlatform, url, newProps)) {
      initialise(
        this.showBanner,
        this.state.nativeBannerSettings,
        url,
        doesPathAcceptsBanner(newProps.storeCode)
      );
    }
  }

  componentDidMount() {
    const { isSupportedPlatform } = this.state,
      url = getCorrespondingAppUrl(isSupportedPlatform, this.props.storeCode);
    if (validate(isSupportedPlatform, url)) {
      initialise(
        this.showBanner,
        this.state.nativeBannerSettings,
        url,
        doesPathAcceptsBanner(this.props.storeCode)
      );
    }
  }

  resetBannerOnTimeout(expireOpenInApp, showBannerFn) {
    return () => {
      const settings = getDefaultPreferences(),
        isHidden = isWindowHidden();
      if (isHidden && expireOpenInApp < Date.now()) {
        set("asos.siteChrome.nativeBanner", settings);
        clearTimeout(timeout);
      } else {
        showBannerFn(settings);
        set("asos.siteChrome.nativeBanner", settings);
      }
    };
  }

  discardBanner() {
    const settings = getDiscardPreferences();
    this.hideBanner(settings, () => {
      set("asos.siteChrome.nativeBanner", settings);
    });
  }

  openInAppCallback() {
    const settings = getOpenAppPreferences();
    const { isSupportedPlatform } = this.state;
    this.hideBanner(settings, () => {
      const url = getCorrespondingAppUrl(
        isSupportedPlatform,
        this.props.storeCode
      );
      if (isIos()) {
        set("asos.siteChrome.nativeBanner", getOpenAppSuccesPreferences());
      } else {
        set("asos.siteChrome.nativeBanner", settings);
      }
      window.location.assign(url);
    });
  }

  handleGetApp() {
    const appStoreUrl = getAppStoreUrl();
    trackNativeAppBannerGetApp(this.state.osPlatform);
    window.location.assign(appStoreUrl);
  }

  hideBanner(withSettings, fn) {
    const nextState = {
      showBanner: { display: "none" },
      nativeBannerSettings: withSettings
    };
    if (null == fn) {
      /* istanbul ignore next ignore anonymous call */
      fn = () => {};
    }
    this.setState(() => nextState, fn);
  }

  showBanner(withSettings) {
    const update = {
      showBanner: { display: "block" }
    };
    const nextState =
      null == withSettings
        ? update
        : Object.assign(update, { nativeBannerSettings: withSettings });
    this.setState(() => nextState);
  }

  render() {
    const { formatTranslation } = this.props,
      appStoreName = isIos() ? "App" : "Play";

    return (
      <div
        className={cx(
          styles.defaultVisibilityBanner,
          styles.asosAppSmartBanner
        )}
        style={this.state.showBanner}
      >
        <div className={styles.sbMessageView}>
          <div className={styles.sbMessageActions}>
            <div className={cx(styles.sbShowRatings, styles.sbMessage)}>
              <div className={styles.sbAppIcon}></div>
              <div className={cx(styles.asosName, deferredStyles.asosNameIcon)}>
                {formatTranslation("nativeappbanner_the_asos_app_text")}
              </div>
              <div>
                {formatTranslation("nativeappbanner_the_asos_appstore_text", {
                  store_market: appStoreName
                })}
              </div>
            </div>
            <div className={styles.sbActions}>
              <button
                id="test-idx"
                onClick={this.openInAppCallback}
                className={cx(styles.sbOpenInApp, styles.sbBtns)}
              >
                {formatTranslation("nativeappbanner_open_in_the_app_text")}
              </button>
              <button
                onClick={this.handleGetApp}
                className={cx(styles.sbInstall, styles.sbBtns)}
              >
                {formatTranslation("nativeappbanner_install_text")}
              </button>
            </div>
          </div>
        </div>
        <button
          onClick={this.discardBanner}
          id="sb-discard"
          className={cx(styles.sbClose, deferredStyles.sbCloseIcon)}
          href="#"
        >
          X
        </button>
      </div>
    );
  }
}

NativeAppBanner.propTypes = {
  welcomeMessage: PropTypes.shape({
    redirectUrl: PropTypes.object,
    country: PropTypes.object,
    browsingLocalStore: PropTypes.bool,
    closeLabel: PropTypes.string,
    platform: PropTypes.string,
    text: PropTypes.string,
    welcome: PropTypes.string
  }),
  storeCode: PropTypes.string.isRequired,
  formatTranslation: PropTypes.func.isRequired
};

export default withTranslation(NativeAppBanner);
